import { Bell, CheckCheck } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { toApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { MenuLabel, Popover } from '@/components/ui/Popover'
import { Spinner } from '@/components/ui/Spinner'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { formatShortDateTime } from '@/lib/format'
import { notificationsService, type InboxNotification } from '@/services/core/notifications.service'
import { useStores } from '@/stores'
import { isPathAllowed } from './access'

const PAGE = 20
const POLL_MS = 60_000

/**
 * Order notifications carry the mobile app's screen (`/orders/<id>`,
 * `/moderator/main/<id>`, `/driver_supervisor/main/<id>`, `/driver/main/<id>`);
 * in the admin they open that order on «Заказы».
 */
const MOBILE_ORDER_SCREEN = /^\/(?:orders|moderator\/main|driver_supervisor\/main|driver\/main)\/([0-9a-f-]{36})(?:[/?#]|$)/i

function adminScreen(screen: string) {
  const order = MOBILE_ORDER_SCREEN.exec(screen)
  return order ? `/sales/orders?sheet=${order[1]}` : screen
}

/**
 * The signed-in profile's inbox (GET /notifications): an unread badge polled
 * every minute and on focus, the latest notifications on open. Opening one
 * marks it read and follows its screen when that is an admin page the
 * profile may open, such as a new work task or the order it is about.
 */
export const NotificationBell = observer(function NotificationBell() {
  const t = useT()
  const navigate = useNavigate()
  const { session, ui } = useStores()
  const enabled = session.can('notifications.read.self')
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState<InboxNotification[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const refreshCount = useCallback(async () => {
    try {
      setUnread(await notificationsService.unreadCount())
    } catch {
      // The badge keeps its last value; the list shows real errors.
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void refreshCount()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refreshCount()
    }, POLL_MS)
    const onFocus = () => void refreshCount()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, refreshCount])

  const load = async (offset: number) => {
    setLoading(true)
    setError('')
    try {
      const page = await notificationsService.list({ lang: ui.contentLocale, limit: PAGE, offset })
      setItems((current) => (offset === 0 ? page.notifications : [...current, ...page.notifications]))
      setHasMore(page.has_more)
    } catch (cause) {
      setError(toApiError(cause).message)
    } finally {
      setLoading(false)
    }
  }

  const canOpen = (item: InboxNotification) => {
    const path = adminScreen(item.screen).split('?')[0]
    return path.startsWith('/') && isPathAllowed(path, session)
  }

  const open = async (item: InboxNotification, close: () => void) => {
    const screen = adminScreen(item.screen)
    if (!item.is_read) {
      try {
        await notificationsService.read(item.guid, ui.contentLocale)
        setItems((current) => current.map((row) => (row.guid === item.guid ? { ...row, is_read: true } : row)))
        setUnread((count) => Math.max(0, count - 1))
      } catch {
        // Following the link still helps; the badge corrects itself on the next poll.
      }
    }
    if (canOpen(item)) {
      close()
      navigate(screen)
    }
  }

  const readAll = async () => {
    try {
      await notificationsService.readAll()
      setItems((current) => current.map((row) => ({ ...row, is_read: true })))
      setUnread(0)
    } catch (cause) {
      setError(toApiError(cause).message)
    }
  }

  if (!enabled) return null

  return (
    <Popover
      align="end"
      kind="dialog"
      trigger={({ toggle, open: expanded, ...aria }) => (
        <Button
          variant="ghost"
          size="icon"
          {...aria}
          onClick={() => {
            if (!expanded) {
              void load(0)
              void refreshCount()
            }
            toggle()
          }}
          aria-label={unread > 0 ? `${t('layout.notifications.open')} · ${t('layout.notifications.unread', { count: unread })}` : t('layout.notifications.open')}
          title={t('layout.notifications.open')}
          className="relative"
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="num absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      )}
    >
      {(close) => (
        <div className="flex w-[min(360px,calc(100vw-32px))] flex-col">
          <div className="flex items-center justify-between gap-2 pr-1">
            {/* A long translation wraps the title, never pushes the button out. */}
            <div className="min-w-0">
              <MenuLabel>
                {t('layout.notifications.title')}
                {unread > 0 && <span className="ml-1.5 text-muted">· {t('layout.notifications.unread', { count: unread })}</span>}
              </MenuLabel>
            </div>
            <Button className="shrink-0" variant="ghost" size="sm" icon={<CheckCheck className="size-3.5" />} disabled={unread === 0} onClick={() => void readAll()}>
              {t('layout.notifications.readAll')}
            </Button>
          </div>
          <div className="max-h-[min(420px,60vh)] overflow-y-auto">
            {error && <p className="px-2.5 py-3 text-[length:calc(12.5px*var(--text-scale))] text-danger">{t('layout.notifications.failed')}: {error}</p>}
            {!error && !loading && items.length === 0 && (
              <p className="px-2.5 py-6 text-center text-[length:calc(13px*var(--text-scale))] text-muted">{t('layout.notifications.empty')}</p>
            )}
            {items.map((item) => (
              <button
                key={item.guid}
                type="button"
                onClick={() => void open(item, close)}
                className="flex w-full gap-2.5 rounded-[calc(8px*var(--radius-scale))] px-2.5 py-2 text-left transition-colors hover:bg-hover"
              >
                <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', item.is_read ? 'bg-transparent' : 'bg-accent')} />
                <span className="min-w-0 flex-1">
                  <span className={cn('block truncate text-[length:calc(13px*var(--text-scale))]', item.is_read ? 'text-fg-2' : 'font-medium text-fg')}>
                    {item.title}
                  </span>
                  {item.message && (
                    <span className="line-clamp-2 whitespace-pre-line text-[length:calc(12px*var(--text-scale))] text-muted">{item.message}</span>
                  )}
                  <span className="flex items-center justify-between gap-2">
                    <span className="num text-[length:calc(11px*var(--text-scale))] text-subtle">{formatShortDateTime(item.created_at)}</span>
                    {canOpen(item) && <span className="text-[length:calc(12px*var(--text-scale))] text-accent">{t('layout.notifications.goTo')}</span>}
                  </span>
                </span>
              </button>
            ))}
            {loading && (
              <div className="flex justify-center py-3">
                <Spinner />
              </div>
            )}
            {!loading && hasMore && (
              <div className="flex justify-center py-1.5">
                <Button variant="ghost" size="sm" onClick={() => void load(items.length)}>
                  {t('layout.notifications.more')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Popover>
  )
})
